import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';
import Icon from 'components/Icon';

const Overlay = styled.div`
  display: ${(props) => (props.visible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  transition: opacity 0.25s linear;
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
  background: rgba(0, 0, 0, 0.6);
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9000;
  text-align: center;
  overflow: auto;
`;

const ModalWindow = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.background?.secondary || '#1a1a2e'
      : '#ffffff'};
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  margin: auto 0;
  position: fixed;
  z-index: 8000;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* Desktop positioning */
  right: 20px;
  top: 2.5vh;
  height: 95dvh;
  max-height: 95dvh;
  width: 40vw;
  max-width: 600px;

  /* Tablet */
  @media (max-width: 1190px) {
    width: 60vw;
    top: 30px;
  }

  /* Mobile */
  @media (max-width: 768px) {
    right: 0;
    top: 0;
    width: 100vw;
    height: 100dvh;
    max-height: 100dvh;
    max-width: 100vw;
    border-radius: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? props.theme.colors?.['border-light'] || 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 26px;
  font-weight: 700;
  margin: 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)'};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.1)'};
  }
`;

const ModalBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: ${(props) => (props.noPadding ? '0' : '24px')};

  /* Themed scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(0, 0, 0, 0.2)'};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(0, 0, 0, 0.3)'};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? props.theme.colors?.['border-light'] || 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  flex-shrink: 0;
`;

export const disableContentScroll = () => {
  const scrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.body.style.width = '100%';
};

export const enableContentScroll = () => {
  if (document.body.style.position === 'fixed') {
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
  }
};

/**
 * POSModal - A themed modal component for POS interfaces
 *
 * Features:
 * - Dark/light theme support
 * - Responsive design (desktop/tablet/mobile)
 * - Customizable header, body, and footer
 * - Portal rendering for proper z-index stacking
 *
 * @param {Object} props
 * @param {boolean} props.isVisible - Controls modal visibility
 * @param {function} props.onClose - Callback when modal is closed
 * @param {string} props.title - Modal title text
 * @param {React.ReactNode} props.children - Modal body content
 * @param {React.ReactNode} props.footer - Optional footer content
 * @param {boolean} props.noPadding - Remove body padding
 * @param {boolean} props.noHeader - Hide the header
 */
const POSModal = ({
  isVisible,
  onClose,
  title,
  children,
  footer,
  noPadding = false,
  noHeader = false,
}) => {
  const [modalRoot, setModalRoot] = useState(null);
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  useEffect(() => {
    setModalRoot(document.getElementById('modal-root'));
  }, []);

  useEffect(() => {
    return () => enableContentScroll();
  }, []);

  useEffect(() => {
    if (isVisible) {
      disableContentScroll();
    } else {
      enableContentScroll();
    }
  }, [isVisible]);

  if (!modalRoot) return null;

  return ReactDOM.createPortal(
    <Overlay visible={isVisible}>
      <ModalWindow visible={isVisible}>
        {!noHeader && (
          <ModalHeader>
            <ModalTitle>{title}</ModalTitle>
            <CloseButton onClick={onClose}>
              <Icon
                icon="close"
                size={isMobile ? 18 : 20}
                color="currentColor"
              />
            </CloseButton>
          </ModalHeader>
        )}
        <ModalBody noPadding={noPadding}>{children}</ModalBody>
        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalWindow>
    </Overlay>,
    modalRoot
  );
};

POSModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node,
  footer: PropTypes.node,
  noPadding: PropTypes.bool,
  noHeader: PropTypes.bool,
};

export default POSModal;

// Export styled components for customization
export { ModalHeader, ModalTitle, ModalBody, ModalFooter, CloseButton };
